import React, { useRef } from 'react';
import {
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
} from '@televet/kibble-ui/build/chakra';
import * as Sentry from '@sentry/react';
import { useUpdateClinicPostcardSettingMutation } from 'shared/types/graphql';
import { useTracking } from 'shared/providers/TrackingProvider';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import { SettingsServiceReminderEvents } from 'shared/providers/TrackingProvider/EventNames';

interface IDisablePostcardsAlertDialogProps {
  isOpen: boolean;
  settingsId: string;
  isPostcardsEnabled: boolean;
  onClose: () => void;
}

const DisablePostcardsAlertDialog = ({
  isOpen,
  onClose,
  isPostcardsEnabled,
  settingsId,
}: IDisablePostcardsAlertDialogProps): JSX.Element => {
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const { trackEvent } = useTracking();
  const toast = useToast();

  const [togglePostcards, { loading }] = useUpdateClinicPostcardSettingMutation({
    variables: {
      where: {
        id: settingsId,
      },
      data: {
        isRemindersEnabled: !isPostcardsEnabled,
      },
    },
    refetchQueries: ['getClinicPostcardsSettings'],
  });

  const handleClick = (): void => {
    try {
      togglePostcards();
    } catch (e) {
      Sentry.captureException(e);
      toast({
        title: `Something went wrong while ${isPostcardsEnabled ? 'disabling' : 'enabling'} the postcards.`,
        status: 'error',
      });
    } finally {
      trackEvent(SettingsServiceReminderEvents.TogglePostcards, { isEnabled: isPostcardsEnabled });
      onClose();
    }
  };

  return (
    <AlertDialog isOpen={isOpen} isCentered={true} leastDestructiveRef={cancelRef} onClose={onClose}>
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="xl" fontWeight="bold">
            Are you sure you want to {isPostcardsEnabled ? 'disable' : 'enable'} postcards?
          </AlertDialogHeader>
          <AlertDialogBody>
            <Text>
              {isPostcardsEnabled
                ? 'By disabling postcards, postcards will not be sent to your clients. Any postcards already in transit will be delivered as scheduled.'
                : "By enabling postcards, you'll be able to check your account balance and start sending postcards."}
            </Text>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button variant="tertiary" onClick={onClose} mr={3}>
              Cancel
            </Button>
            <Button
              variant={isPostcardsEnabled ? 'primaryDestructive' : 'primary'}
              onClick={handleClick}
              isLoading={loading}
            >
              {isPostcardsEnabled ? 'Disable postcards' : 'Enable postcards'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

export default DisablePostcardsAlertDialog;
